/* assets/luxury-home.js */
(() => {
  "use strict";

  /*
   * The file can be loaded by several sections. Later copies only ask the
   * first one to scan the document again; no listener is added twice.
   */
  if (window.LuxuryHome) {
    window.LuxuryHome.init(document);
    return;
  }

  const SELECTORS = {
    carousel: "[data-luxury-carousel]",
    track: "[data-luxury-track]",
    item: "[data-luxury-item]",
    previous: "[data-luxury-prev]",
    next: "[data-luxury-next]",
    dot: "[data-luxury-slide]",
    nativeForm: "product-form[data-luxury-native-form]",
    favorite: "button[data-luxury-favorite]"
  };

  const EDGE_TOLERANCE = 2;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  /* Carousel element -> { destroy }. Prevents double initialisation. */
  const carousels = new WeakMap();

  let nativeFormPromise = null;

  /* ---------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------ */

  function toNumber(value) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  function scrollBehavior() {
    return reducedMotion.matches ? "auto" : "smooth";
  }

  /* Returns the root itself (if it matches) plus its matching descendants. */
  function findAll(root, selector) {
    if (!root || typeof root.querySelectorAll !== "function") return [];

    const matches = [];

    if (typeof root.matches === "function" && root.matches(selector)) {
      matches.push(root);
    }

    return matches.concat(Array.from(root.querySelectorAll(selector)));
  }

  /* ---------------------------------------------------------------------
   * Favorites
   *
   * Stored in this browser only (localStorage): no account, no list page.
   * If storage is blocked, favorites still work until the page is closed.
   * ------------------------------------------------------------------ */

  const FAVORITES_KEY = "luxury:favorites";
  const FAVORITES_EVENT = "luxury:favorites-change";

  let memoryFavorites = [];
  let storageFailed = false;

  function sanitizeFavorites(value) {
    if (!Array.isArray(value)) return [];

    return value.filter(
      (entry) => entry && typeof entry.id === "string" && entry.id !== ""
    );
  }

  function readFavorites() {
    if (storageFailed) return memoryFavorites.slice();

    try {
      const raw = window.localStorage.getItem(FAVORITES_KEY);
      return raw ? sanitizeFavorites(JSON.parse(raw)) : [];
    } catch (error) {
      storageFailed = true;
      return memoryFavorites.slice();
    }
  }

  function writeFavorites(list) {
    memoryFavorites = list.slice();
    if (storageFailed) return;

    try {
      window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(list));
    } catch (error) {
      storageFailed = true;
    }
  }

  /*
   * Reveals the buttons (they are rendered hidden so that they never show
   * without this script) and mirrors the saved state in aria-pressed.
   */
  function refreshFavoriteButtons(root, list) {
    const saved = new Set((list || readFavorites()).map((entry) => entry.id));

    findAll(root, SELECTORS.favorite).forEach((button) => {
      button.hidden = false;
      button.setAttribute(
        "aria-pressed",
        saved.has(button.dataset.productId) ? "true" : "false"
      );
    });
  }

  function toggleFavorite(button) {
    const id = button.dataset.productId;
    if (!id) return;

    const handle = button.dataset.productHandle || "";
    const list = readFavorites();
    const index = list.findIndex((entry) => entry.id === id);
    let active;

    if (index >= 0) {
      list.splice(index, 1);
      active = false;
    } else {
      list.push({ id, handle });
      active = true;
    }

    writeFavorites(list);

    /* Every card of the same product, in any section, stays in sync. */
    refreshFavoriteButtons(document, list);

    document.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT, {
        detail: { id, handle, active, count: list.length }
      })
    );
  }

  /* ---------------------------------------------------------------------
   * Native Dawn product-form component
   * ------------------------------------------------------------------ */

  /* Only same-origin or Shopify CDN scripts are accepted. */
  function resolveTrustedUrl(value) {
    if (!value) return null;

    try {
      const url = new URL(value, location.href);
      const webProtocol = url.protocol === "https:" || url.protocol === "http:";
      const trusted =
        url.origin === location.origin || url.hostname === "cdn.shopify.com";

      return webProtocol && trusted ? url : null;
    } catch (error) {
      return null;
    }
  }

  function isSameScript(script, url) {
    if (!script.src) return false;

    try {
      const other = new URL(script.src, location.href);
      return other.origin === url.origin && other.pathname === url.pathname;
    } catch (error) {
      return false;
    }
  }

  function loadNativeProductForm(root) {
    if (customElements.get("product-form")) return Promise.resolve();

    const form = findAll(root, SELECTORS.nativeForm)[0];
    if (!form) return Promise.resolve();

    if (nativeFormPromise) return nativeFormPromise;

    const url = resolveTrustedUrl(form.dataset.productFormUrl);
    if (!url) return Promise.resolve();

    nativeFormPromise = new Promise((resolve) => {
      const alreadyPresent = Array.from(document.scripts).some((script) =>
        isSameScript(script, url)
      );

      if (alreadyPresent) {
        /*
         * Dawn may already be loading its native component.
         * Do not insert a second copy.
         */
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = url.href;
      script.async = true;
      script.dataset.luxuryNativeProductForm = "true";

      script.addEventListener("load", resolve, { once: true });
      script.addEventListener(
        "error",
        () => {
          /*
           * Without the native component, the Shopify product form
           * remains a regular form submission.
           */
          nativeFormPromise = null;
          resolve();
        },
        { once: true }
      );

      document.head.appendChild(script);
    });

    return nativeFormPromise;
  }

  /* ---------------------------------------------------------------------
   * Carousels
   * ------------------------------------------------------------------ */

  function createCarousel(carousel) {
    const track = carousel.querySelector(SELECTORS.track);
    if (!track) return null;

    const previous = carousel.querySelector(SELECTORS.previous);
    const next = carousel.querySelector(SELECTORS.next);
    const dots = Array.from(carousel.querySelectorAll(SELECTORS.dot));

    const controller = new AbortController();
    const { signal } = controller;

    let frame = null;
    let resizeObserver = null;

    function getItems() {
      return Array.from(track.querySelectorAll(SELECTORS.item));
    }

    function isRtl() {
      return getComputedStyle(track).direction === "rtl";
    }

    /* Distance between the start edge of the scrollport and an item. */
    function offsetOf(item) {
      const style = getComputedStyle(track);
      const rtl = style.direction === "rtl";
      const padding = toNumber(
        rtl ? style.scrollPaddingRight : style.scrollPaddingLeft
      );
      const trackRect = track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      return rtl
        ? itemRect.right - (trackRect.right - padding)
        : itemRect.left - (trackRect.left + padding);
    }

    function nearestIndex(items) {
      let nearest = 0;
      let smallest = Infinity;

      items.forEach((item, index) => {
        const distance = Math.abs(offsetOf(item));

        if (distance < smallest) {
          smallest = distance;
          nearest = index;
        }
      });

      return nearest;
    }

    /* Real card width plus the real gap, measured from the layout. */
    function itemPitch(items) {
      if (items.length > 1) {
        const first = items[0].getBoundingClientRect();
        const second = items[1].getBoundingClientRect();
        const pitch = Math.abs(second.left - first.left);

        if (pitch > 0) return pitch;
      }

      return items.length ? items[0].getBoundingClientRect().width : 0;
    }

    /* Scroll distance of one "page": the number of fully visible cards. */
    function pageDistance(items) {
      const pitch = itemPitch(items);
      if (!(pitch > 0)) return track.clientWidth;

      const style = getComputedStyle(track);
      const available =
        track.clientWidth -
        toNumber(style.paddingLeft) -
        toNumber(style.paddingRight);
      const itemWidth = items[0].getBoundingClientRect().width;
      const gap = Math.max(0, pitch - itemWidth);
      const visible = Math.max(
        1,
        Math.floor((available + gap) / pitch + 0.01)
      );

      return visible * pitch;
    }

    function scrollPage(direction) {
      const items = getItems();
      if (!items.length) return;

      const sign = isRtl() ? -1 : 1;

      track.scrollBy({
        left: direction * sign * pageDistance(items),
        behavior: scrollBehavior()
      });
    }

    function goTo(index) {
      const items = getItems();
      if (!items.length) return;

      const targetIndex = Math.max(0, Math.min(items.length - 1, index));

      track.scrollBy({
        left: offsetOf(items[targetIndex]),
        behavior: scrollBehavior()
      });
    }

    function updateControls() {
      const items = getItems();
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = Math.abs(track.scrollLeft);
      const overflow = items.length > 0 && maxScroll > EDGE_TOLERANCE;

      const atStart = !overflow || position <= EDGE_TOLERANCE;
      const atEnd = !overflow || position >= maxScroll - EDGE_TOLERANCE;

      const focused = document.activeElement;

      if (previous) previous.disabled = atStart;
      if (next) next.disabled = atEnd;

      /* Keep keyboard focus on a usable arrow when one becomes disabled. */
      if (focused === previous && atStart && next && !atEnd) {
        next.focus({ preventScroll: true });
      } else if (focused === next && atEnd && previous && !atStart) {
        previous.focus({ preventScroll: true });
      }

      if (dots.length) {
        const activeIndex = items.length ? nearestIndex(items) : -1;

        dots.forEach((dot, index) => {
          dot.setAttribute(
            "aria-current",
            index === activeIndex ? "true" : "false"
          );
        });
      }
    }

    function scheduleUpdate() {
      if (frame !== null) return;

      frame = requestAnimationFrame(() => {
        frame = null;
        updateControls();
      });
    }

    if (previous) {
      previous.addEventListener("click", () => scrollPage(-1), { signal });
    }

    if (next) {
      next.addEventListener("click", () => scrollPage(1), { signal });
    }

    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => goTo(index), { signal });
    });

    track.addEventListener(
      "keydown",
      (event) => {
        /*
         * Do not intercept keyboard events from links or buttons
         * inside product cards.
         */
        if (event.target !== track) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;

        const items = getItems();
        if (!items.length) return;

        const rtl = isRtl();
        let targetIndex = nearestIndex(items);

        if (event.key === "ArrowRight") {
          targetIndex += rtl ? -1 : 1;
        } else if (event.key === "ArrowLeft") {
          targetIndex += rtl ? 1 : -1;
        } else if (event.key === "Home") {
          targetIndex = 0;
        } else if (event.key === "End") {
          targetIndex = items.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        goTo(targetIndex);
      },
      { signal }
    );

    track.addEventListener("scroll", scheduleUpdate, {
      passive: true,
      signal
    });

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(track);
    } else {
      window.addEventListener("resize", scheduleUpdate, { signal });
    }

    updateControls();

    return {
      destroy() {
        controller.abort();

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        if (frame !== null) {
          cancelAnimationFrame(frame);
          frame = null;
        }
      }
    };
  }

  function initCarousel(carousel) {
    if (carousels.has(carousel)) return;

    const instance = createCarousel(carousel);
    if (!instance) return;

    carousels.set(carousel, instance);
    carousel.dataset.luxuryReady = "true";
    carousel.luxuryCleanup = () => destroyCarousel(carousel);
  }

  function destroyCarousel(carousel) {
    const instance = carousels.get(carousel);
    if (!instance) return;

    instance.destroy();
    carousels.delete(carousel);
    delete carousel.dataset.luxuryReady;
    delete carousel.luxuryCleanup;
  }

  /* ---------------------------------------------------------------------
   * Public API and theme editor events
   * ------------------------------------------------------------------ */

  function init(root) {
    loadNativeProductForm(root);
    findAll(root, SELECTORS.carousel).forEach(initCarousel);
    refreshFavoriteButtons(root);
  }

  function destroy(root) {
    findAll(root, SELECTORS.carousel).forEach(destroyCarousel);
  }

  window.LuxuryHome = { init, destroy, getFavorites: readFavorites };

  /* One delegated listener: it survives section reloads in the theme editor. */
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(SELECTORS.favorite);
    if (button) toggleFavorite(button);
  });

  /* Another tab changed the favorites: mirror them here. */
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === FAVORITES_KEY) {
      refreshFavoriteButtons(document);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document), {
      once: true
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });
})();